import { Fragment, useMemo } from "react";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { PurchaseOrderListItem } from "../../types/purchase-order.type";
import { numberWithCommas } from "../../utils";
import PurchaseOrderPagination from "./Pagination";
import PurchaseOrderRow from "./Row";
import PurchaseOrderSkeleton from "./Skeleton";
import {
  buildRouteGroups,
  PO_PUBLISH_MAX_UUIDS,
  PurchaseOrderMatching,
  PurchaseOrderRouteGroup,
  PurchaseOrderSelection,
} from "./data";

const CELL_PADDING = 8;

/** ความสูงของแถวจริง (สองบรรทัดข้อความ + padding) ที่ skeleton ต้องสูงเท่ากัน */
const ROW_HEIGHT = 44 + CELL_PADDING * 2;

interface PurchaseOrderTableProps {
  rows: PurchaseOrderListItem[];
  loading: boolean;
  totalCount: number;
  page: number;
  limit: number;
  onChangePage: (page: number) => void;
  onChangeLimit: (limit: number) => void;
  onOpenDetail: (uuid: string) => void;
  /** ติ๊กเลือกใบ — W1 (ส่ง/ระบุสาขา/ดึงกลับ) และ W7 (ส่งคืน HO) */
  selection?: PurchaseOrderSelection;
  /** W7 เท่านั้น: ปุ่ม ส่งคืน HO รายแถว (แสดงเฉพาะใบที่ส่งคืนได้) */
  onReturnRow?: (row: PurchaseOrderListItem) => void;
  /** W7 เท่านั้น: จัดกลุ่มแถวตามสายของร้าน */
  groupByRoute?: boolean;
  /** W7 เท่านั้น: แผงจับคู่ร้านใต้แถวที่ยังไม่มีร้าน */
  matching?: PurchaseOrderMatching;
}

/**
 * ตารางรายการ PO ที่ W1 (HO ทุกสาขา) และ W7 (PO ของสาขา) ใช้ร่วมกัน
 *
 * คอลัมน์สายอ่านจากสายหลักของร้านตอนแสดงผล ไม่ได้เก็บบน PO จึงแก้จากจอนี้ไม่ได้
 * ทั้งสองจอ และการแบ่งหน้าทำที่ server ตารางจึงแสดงเฉพาะแถวที่ได้รับมาเท่านั้น
 */
export default function PurchaseOrderTable({
  rows,
  loading,
  totalCount,
  page,
  limit,
  onChangePage,
  onChangeLimit,
  onOpenDetail,
  selection,
  groupByRoute = false,
  matching,
  onReturnRow,
}: PurchaseOrderTableProps) {
  const columnCount = selection ? 8 : 7;

  // ใบที่เลือกได้ในหน้านี้ตามกติกาของจอ · ตัดที่เพดาน uuid ต่อการส่งหนึ่งครั้ง เพราะ
  // "เลือกทั้งหน้า" ต้องไม่สร้างคำขอที่เกินกว่าที่ API รับได้
  const canSelect = selection?.canSelect;
  const publishableUuids = useMemo(
    () =>
      canSelect
        ? rows
            .filter(canSelect)
            .map((row) => row.uuid)
            .slice(0, PO_PUBLISH_MAX_UUIDS)
        : [],
    [rows, canSelect],
  );

  const groups: PurchaseOrderRouteGroup[] = useMemo(() => {
    if (groupByRoute) return buildRouteGroups(rows);
    return [{ key: "all", label: "", rows }];
  }, [groupByRoute, rows]);

  const selectedCountOnPage = publishableUuids.filter((uuid) =>
    selection?.selected.has(uuid),
  ).length;
  const allSelectedOnPage =
    publishableUuids.length > 0 &&
    selectedCountOnPage === publishableUuids.length;

  const handleToggleSelectAll = () => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (allSelectedOnPage) {
      publishableUuids.forEach((uuid) => next.delete(uuid));
    } else {
      publishableUuids.forEach((uuid) => next.add(uuid));
    }
    selection.onChange(next);
  };

  const handleToggleSelectRow = (uuid: string) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    if (next.has(uuid)) next.delete(uuid);
    else next.add(uuid);
    selection.onChange(next);
  };

  // ตารางกินเต็มความกว้างของการ์ด (theme ใส่ padding 16px ให้ Paper ทุกใบ) ส่วน
  // overflow hidden ทำให้หัวตารางถูกตัดตามมุมโค้งของการ์ด
  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {selection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelectedOnPage}
                    indeterminate={
                      selectedCountOnPage > 0 && !allSelectedOnPage
                    }
                    disabled={publishableUuids.length === 0}
                    onChange={handleToggleSelectAll}
                    inputProps={{ "aria-label": "เลือกทุกใบในหน้านี้" }}
                  />
                </TableCell>
              )}
              <TableCell>เลข PO</TableCell>
              <TableCell>วันส่ง</TableCell>
              <TableCell>สาขา</TableCell>
              <TableCell>สาย</TableCell>
              <TableCell>ร้าน</TableCell>
              <TableCell align="right">จำนวนเงิน</TableCell>
              <TableCell align="center">สถานะ</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <PurchaseOrderSkeleton
                columns={columnCount}
                rows={limit}
                rowHeight={ROW_HEIGHT}
              />
            )}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              groups.map((group) => (
                <Fragment key={group.key}>
                  {group.label && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        sx={{
                          bgcolor: (theme) => theme.palette.background.default,
                          py: 0.75,
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {group.label} · {numberWithCommas(group.rows.length)}{" "}
                          ใบ
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {group.rows.map((row, rowIndex) => (
                    <PurchaseOrderRow
                      key={row.uuid}
                      row={row}
                      striped={rowIndex % 2 === 1}
                      columnCount={columnCount}
                      onOpenDetail={onOpenDetail}
                      selected={selection?.selected.has(row.uuid)}
                      onToggleSelect={
                        selection ? handleToggleSelectRow : undefined
                      }
                      canSelect={selection?.canSelect(row) ?? false}
                      disabledReason={
                        selection?.disabledReason?.(row) ?? null
                      }
                      matching={matching}
                      onReturn={onReturnRow}
                    />
                  ))}
                </Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PurchaseOrderPagination
        totalCount={totalCount}
        page={page}
        limit={limit}
        onChangePage={onChangePage}
        onChangeLimit={onChangeLimit}
      />
    </Paper>
  );
}
